<template>
  <form
    :action="formUrl"
    id="offerSearch"
    ref="offerSearchForm"
    method="get"
    @submit.prevent="submitForm"
  >
    <div class="container-padded pt-12 pb-4">
      <h2 class="text-5xl text-center font-light text-primary-800">
        {{ lang.pages.search.form_headline }}
      </h2>
      <div class="pt-8">
        <input-text
          :placeholder="lang.sPlaceholder"
          id="s"
          name="s"
          :tabindex="1"
          v-model="s"
        />
      </div>

      <div class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="space-y-4">
          <!-- Main Category -->
          <div class="">
            <input-select
              id="cat-main"
              name="cat-main"
              :tabindex="2"
              :label="lang.labels.main_category"
              :options="catMainOptions"
              :emptyFirstOption="lang.labels.please_select"
              v-model="catMain"
            ></input-select>
          </div>

          <!-- Sub Category -->
          <div class="">
            <input-select
              id="cat-sub"
              name="cat-sub"
              :tabindex="3"
              :label="lang.labels.sub_category"
              :options="catSubOptions"
              :emptyFirstOption="lang.labels.please_select"
              v-model="catSub"
              :disabled="disableSubCategorySelect"
            ></input-select>
          </div>
        </div>

        <div class="space-y-4">
          <!-- Manufacturer -->
          <div class="">
            <input-select-with-search
              :label="lang.labels.manufacturer"
              :options="manufacturerOptions"
              v-model="manufacturer"
              name="manufacturer"
              id="manufacturer"
            ></input-select-with-search>
          </div>

          <!-- discount -->
          <div class="">
            <input-select
              id="discount"
              name="discount"
              :tabindex="4"
              :label="lang.labels.sort_discount_desc"
              :options="discountOptions"
              v-model="discount"
            ></input-select>
          </div>
        </div>

        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <!-- Price low -->
            <div class="">
              <input-text
                :label="lang.labels.price_low"
                id="price-low"
                name="price-low"
                :tabindex="5"
                type="number"
                v-model="priceLow"
              />
            </div>

            <!-- Price high -->
            <div class="">
              <input-text
                :label="lang.labels.price_heigh"
                id="price-high"
                name="price-high"
                :tabindex="6"
                type="number"
                v-model="priceHigh"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <!-- Zip area -->
            <div class="">
              <input-text
                :label="lang.labels.zip_area"
                id="zip"
                name="zip"
                :tabindex="7"
                v-model="zip"
              />
            </div>

            <!-- Zip radius -->
            <div class="">
              <input-select
                id="zip-radius"
                name="zip-radius"
                :tabindex="8"
                :label="lang.labels.zip_radius"
                :options="zipRadiusOptions"
                :emptyFirstOption="lang.labels.please_select"
                v-model="zipRadius"
              ></input-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Show seller filter -->
      <div class="mt-4" v-if="seller !== ''">
        <badge-with-remove-button
          @delete="resetSellerFilter"
          :label="sellerName"
        />
      </div>

      <div class="mt-4 pt-4 border-gray-300 border-t">
        <div
          class="flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0"
        >
          <div>
            <a class="plain" @click.prevent="resetForm" href="#"
              ><small>{{ lang.labels.reset_search }}</small></a
            >
          </div>
          <div
            class="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
          >
            <plain-button
              class="w-full sm:w-48"
              @click="saveCurrentSearch"
              color="warning"
              >{{ lang.labels.save_search }}</plain-button
            >
            <plain-button
              class="w-full sm:w-48"
              type="submit"
              color="primary"
              button-type="submit"
              >{{ lang.labels.find }}</plain-button
            >
          </div>
        </div>
      </div>
    </div>

    <input name="seller" type="hidden" id="seller" :value="seller" />
    <input name="sort" type="hidden" id="sort" :value="sort" />

    <modal-master-guest ref="modalMasterGuest"></modal-master-guest>
    <modal-master-user ref="modalMasterUser" v-if="!guest"></modal-master-user>
  </form>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import axios from "axios";
import _ from "lodash";

import InputText from "../inputs/InputText";
import InputSelect from "../inputs/InputSelect";
import BadgeWithRemoveButton from "../badges/BadgeWithRemoveButton";
import PlainButton from "../buttons/PlainButton";
import InputSelectWithSearch from "../inputs/InputSelectWithSearch.vue";
import ModalMasterGuest from "../modal/ModalMasterGuest.vue";
import ModalMasterUser from "../modal/ModalMasterUser.vue";

const props = defineProps({
  initSeller: String,
  initSort: String,
  initS: String,
  initCatMain: Number,
  initCatSub: Number,
  initManufacturer: Number,
  initDiscount: Number,
  initPriceLow: String,
  initPriceHigh: String,
  initZip: String,
  initZipRadius: String,
  initMainCategories: [Array, Object],
  initSubCategories: [Array, Object],
  initManufacturers: [Array, Object],

  guest: Boolean,
  sellerName: String,
  formUrl: String,
  lang: Object,
  queryData: Object,
});

const emit = defineEmits([]);

// STATE
const seller = ref(props.initSeller);
const sort = ref(props.initSort);
const s = ref(props.initS);
const catMain = ref(props.initCatMain);
const catSub = ref(props.initCatSub);
const manufacturer = ref(props.initManufacturer);
const discount = ref(props.initDiscount);
const priceLow = ref(props.initPriceLow);
const priceHigh = ref(props.initPriceHigh);
const zip = ref(props.initZip);
const zipRadius = ref(props.initZipRadius);

const mainCategories = ref(props.initMainCategories);
const subCategories = ref(props.initSubCategories);
const manufacturers = ref(props.initManufacturers);

// REFS
const offerSearchForm = ref(null);
const modalMasterUser = ref(null);
const modalMasterGuest = ref(null);


// determine if subcategories dropDown needs to be disabled
const disableSubCategorySelect = computed(() => {
  if (subCategories.value === "" || subCategories.value.length < 1) {
    return true;
  }

  return false;
});

// main categories sorted
const mainCategoriesSorted = computed(() => {
  return _.sortBy(mainCategories.value, (item) => {
    return item.name;
  });
});

// sub-categories sorted
const subCategoriesSorted = computed(() => {
  return _.sortBy(subCategories.value, (item) => {
    return `${item.sort_order}${item.name}`;
  });
});

const catMainOptions = computed(() => {
  return _.map(mainCategoriesSorted.value, function (mainCategory) {
    return { value: mainCategory.id, name: mainCategory.name };
  });
});

const catSubOptions = computed(() => {
  return _.map(subCategoriesSorted.value, function (subCategory) {
    return { value: subCategory.id, name: subCategory.name };
  });
});

const manufacturerOptions = computed(() => {
  return _.map(manufacturers.value, function (manufacturerItem) {
    return { value: manufacturerItem.id, name: manufacturerItem.name };
  });
});

const discountOptions = computed(() => {
  return [
    {
      value: 0,
      name: props.lang.labels.discountNoFilter,
    },
    {
      value: 25,
      name: props.lang.labels.discount25,
    },
    {
      value: 50,
      name: props.lang.labels.discount50,
    },
  ];
});

const zipRadiusOptions = computed(() => {
  return [
    {
      value: 25,
      name: "25 km",
    },
    {
      value: 50,
      name: "50 km",
    },
    {
      value: 100,
      name: "100 km",
    },
  ];
});

// fetch sub categories
const fetchSubCategories = () => {
  subCategories.value = [];

  if (!catMain.value || catMain.value === "" || catMain.value === 0) {
    return;
  }

  // fetch sub categories for main categrory
  axios
    .get(`/web-api/subCategories`, {
      params: {
        main_category_id: catMain.value,
      },
    })
    .then((response) => {
      subCategories.value = response.data.subCategories;
      reRenderSubCategories();
    })
    .catch((error) => {
      // console.log(error)
    });
};

/**
 * reset form data
 */
const resetForm = () => {
  seller.value = "";
  sort.value = "";
  s.value = "";
  catMain.value = 0;
  catSub.value = 0;
  manufacturer.value = 0;
  discount.value = 0;
  priceLow.value = "";
  priceHigh.value = "";
  zip.value = "";
  zipRadius.value = "";
};

/**
 * reset seller filter
 */
const resetSellerFilter = () => {
  seller.value = "";
};

/**
 * re-render sub categories
 */
const reRenderSubCategories = () => {
  /*setTimeout(() => {
                $('#cat-sub').selectpicker('refresh');
            }, 300);*/
};

// submit search froem
const submitForm = () => {
  sort.value = s.value ? "relevance" : "date-desc";

  setTimeout(() => {
    offerSearchForm.value.submit();
  }, 200);
};

// check if the user is logged in
const checkUserLogin = () => {
  if (props.guest) {
    showRegisterModal("saveSearch");
    return false;
  }

  return true;
};

/**
 * load register modal
 */
const showRegisterModal = (trigger) => {
  let modalData = {
    modalTrigger: trigger,
  };

  if (
    modalMasterGuest.value &&
    typeof modalMasterGuest.value.loadModal === "function"
  ) {
    modalMasterGuest.value.loadModal("guestRegister", modalData);
  } else {
    console.error(
      "modalMasterGuest ref is not properly initialized or loadModal method is not available"
    );
  }
};

// save current search
const saveCurrentSearch = () => {
  if (!checkUserLogin()) {
    return;
  }

  let modalData = {
    searchQuery: [
      {
        name: "seller",
        value: seller.value,
      },
      {
        name: "sort",
        value: sort.value,
      },
      {
        name: "s",
        value: s.value,
      },
      {
        name: "cat-main",
        value: catMain.value,
      },
      {
        name: "cat-sub",
        value: catSub.value,
      },
      {
        name: "manufacturer",
        value: manufacturer.value,
      },
      {
        name: "discount",
        value: discount.value,
      },
      {
        name: "price-low",
        value: priceLow.value,
      },
      {
        name: "price-high",
        value: priceHigh.value,
      },
      {
        name: "zip",
        value: zip.value,
      },
      {
        name: "zip-radius",
        value: zipRadius.value,
      },
    ],
  };

  if (
    modalMasterUser.value &&
    typeof modalMasterUser.value.loadModal === "function"
  ) {
    modalMasterUser.value.loadModal("saveSearch", modalData);
  } else {
    console.error(
      "modalMasterUser ref is not properly initialized or loadModal method is not available"
    );
  }
};

// watch catMain change
watch(catMain, () => {
  fetchSubCategories();
});
</script>
